import { useCallback, useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import apiService from '../../services/api';

export type GoogleContinueSuccess = {
  accessToken: string;
  refreshToken?: string;
  userId: string;
  username: string;
};

type Props = {
  onSuccess: (result: GoogleContinueSuccess) => void;
};

type GoogleStatus = { enabled?: boolean; useDevStub?: boolean };

/**
 * Continue with Google on MailBox Login. Popup + handoff redeem (no raw JWT in postMessage).
 */
export default function GoogleContinueButton({ onSuccess }: Props) {
  const [enabled, setEnabled] = useState(false);
  const [useDevStub, setUseDevStub] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stubEmail, setStubEmail] = useState('');

  useEffect(() => {
    apiService
      .get<GoogleStatus>('/api/Auth/google/status')
      .then((data) => {
        setEnabled(!!data?.enabled);
        setUseDevStub(!!data?.useDevStub);
      })
      .catch(() => setEnabled(false));
  }, []);

  const startGoogle = useCallback(async () => {
    setError(null);
    setBusy(true);
    const apiOrigin = window.location.origin;
    const qs = new URLSearchParams({
      acceptTerms: 'true',
      loginContext: 'mailbox',
      checkoutContext: 'mailbox',
      openerOrigin: window.location.origin,
      returnUrl: window.location.href,
    });
    if (useDevStub && stubEmail.trim()) qs.set('stubEmail', stubEmail.trim());

    const startUrl = `/api/Auth/google?${qs.toString()}`;
    const popup = window.open(startUrl, 'qixit_google_auth', 'width=520,height=640');
    if (!popup) {
      setBusy(false);
      setError('Popup blocked. Allow popups, or use the full-page link below.');
      return;
    }

    let settled = false;
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(closedPoll);
    };

    const onMessage = async (event: MessageEvent) => {
      // In dev, Vite proxies /api so postMessage origin is still the mailbox-web origin
      // when the callback HTML is served through the proxy; when callback hits :5220 directly,
      // origin is the API host. Accept either.
      const allowed =
        event.origin === apiOrigin ||
        event.origin === 'http://127.0.0.1:5220' ||
        event.origin === 'http://localhost:5220';
      if (!allowed) return;
      const data = event.data;
      if (!data || data.type !== 'googleAuth' || !data.handoffId) return;
      if (settled) return;
      settled = true;
      cleanup();
      try {
        const handoff = await apiService.get<{
          accessToken: string;
          refreshToken?: string;
          userId: string;
          username: string;
        }>(`/api/Auth/social-handoff/${encodeURIComponent(data.handoffId)}`);
        if (!handoff?.accessToken || !handoff.userId || !handoff.username) {
          throw new Error('Invalid handoff response');
        }
        onSuccess({
          accessToken: handoff.accessToken,
          refreshToken: handoff.refreshToken,
          userId: handoff.userId,
          username: handoff.username,
        });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Google sign-in failed');
      } finally {
        setBusy(false);
        try {
          popup.close();
        } catch {
          /* ignore */
        }
      }
    };

    window.addEventListener('message', onMessage);
    const closedPoll = window.setInterval(() => {
      if (popup.closed && !settled) {
        settled = true;
        cleanup();
        setBusy(false);
      }
    }, 500);
  }, [onSuccess, stubEmail, useDevStub]);

  if (!enabled) return null;

  return (
    <Box sx={{ width: '100%', mb: 2 }} data-testid="google-continue">
      <Button
        variant="outlined"
        fullWidth
        disabled={busy}
        onClick={() => void startGoogle()}
        sx={{ textTransform: 'none', mb: 1 }}
        data-testid="google-continue-button"
      >
        {busy ? 'Waiting for Google…' : 'Continue with Google'}
      </Button>
      {useDevStub && (
        <TextField
          fullWidth
          size="small"
          type="email"
          label="Dev stub email"
          value={stubEmail}
          onChange={(e) => setStubEmail(e.target.value)}
          sx={{ mb: 1 }}
          inputProps={{ 'data-testid': 'google-stub-email' }}
        />
      )}
      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }} data-testid="google-continue-error">
          {error}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        By continuing you agree to the Terms and Privacy Policy.
      </Typography>
      <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 1 }}>
        OR
      </Typography>
    </Box>
  );
}
